import { useCallback, useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { InlineMessage } from '@/components/inline-message/InlineMessage'
import { Modal, ModalContent, ModalFooterWithActions } from '@/components/modal'
import { kMaxCustomInstructionsChars } from '@/settings/schemas/settings-schema'

type CustomInstructionsSettingsDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialInstructions: string
  onSave: (customInstructions: string) => Promise<void>
}

const kMinimumTextAreaHeight = 144
const kMaximumTextAreaHeight = 560

function resizeTextArea(textArea: HTMLTextAreaElement) {
  if (textArea.offsetWidth === 0) return

  textArea.style.height = '0px'
  textArea.style.height = `${Math.min(Math.max(textArea.scrollHeight, kMinimumTextAreaHeight), kMaximumTextAreaHeight)}px`
}

export default function CustomInstructionsSettingsDialog(props: CustomInstructionsSettingsDialogProps) {
  const { open, onOpenChange, initialInstructions, onSave } = props
  const [draft, setDraft] = useState(initialInstructions)
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState(false)
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null)

  const hasReachedCharacterLimit = draft.length >= kMaxCustomInstructionsChars
  const isOverLimit = draft.length > kMaxCustomInstructionsChars
  const containsBlockTag = /<\/?\s*custom_instructions\s*>/i.test(draft)
  const isDirty = draft !== initialInstructions
  const canSave = isDirty && !isOverLimit && !containsBlockTag && !isSaving
  const remainingCharacters = Math.max(kMaxCustomInstructionsChars - draft.length, 0)
  const inputDescriptionIds = [hasReachedCharacterLimit && 'custom-instructions-character-limit', containsBlockTag && 'custom-instructions-validation']
    .filter(Boolean)
    .join(' ')

  const handleTextAreaRef = useCallback((textArea: HTMLTextAreaElement | null) => {
    textAreaRef.current = textArea
    if (!textArea) return

    requestAnimationFrame(() => resizeTextArea(textArea))
  }, [])

  useEffect(() => {
    if (!open) return

    setDraft(initialInstructions)
    setIsSaving(false)
    setSaveError(false)
  }, [initialInstructions, open])

  useEffect(() => {
    const textArea = textAreaRef.current
    if (!open || !textArea) return

    const frame = requestAnimationFrame(() => resizeTextArea(textArea))
    return () => cancelAnimationFrame(frame)
  }, [draft, open])

  const handleSave = async () => {
    if (!canSave) return

    setIsSaving(true)
    setSaveError(false)

    try {
      await onSave(draft)
      onOpenChange(false)
    } catch {
      setSaveError(true)
    } finally {
      setIsSaving(false)
    }
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (isSaving) return
    onOpenChange(nextOpen)
  }

  return (
    <Modal open={open} onOpenChange={handleOpenChange}>
      <ModalContent
        title="Custom instructions"
        subtitle="Describe how you work and how Paxton should respond."
        width="md"
        height="max-xl"
        className="max-w-[568px]"
        bodyClassName="flex flex-col overflow-hidden pt-3"
        preventDismiss={isSaving}
        // Opened from within the SettingsModal, so stack above its overlay/content (repo's second-dialog pattern).
        stacked
        footer={
          <ModalFooterWithActions
            inlineMessage={
              saveError
                ? {
                    variant: 'error',
                    children: 'Failed to save your custom instructions. Your text is still here — try again.',
                  }
                : undefined
            }
            secondaryAction={{ label: 'Cancel', onClick: () => onOpenChange(false), disabled: isSaving }}
            primaryAction={{ label: 'Save', onClick: handleSave, loading: isSaving, disabled: !canSave }}
          />
        }
      >
        <div className="flex min-h-0 flex-1 flex-col gap-2">
          <textarea
            ref={handleTextAreaRef}
            id="custom-instructions"
            aria-label="Custom instructions"
            aria-invalid={isOverLimit || containsBlockTag}
            aria-describedby={inputDescriptionIds || undefined}
            className={clsx(
              'min-h-36 max-h-[560px] w-full resize-none overflow-y-auto rounded border-0 p-3 text-base leading-6 tracking-[-0.32px] text-text-primary ring-1 ring-inset placeholder:text-text-tertiary focus:outline-hidden focus:ring-inset',
              hasReachedCharacterLimit || containsBlockTag ? 'ring-red-500 focus:ring-red-500' : 'ring-border-field focus:ring-brand-400'
            )}
            placeholder="What should Paxton know about how you work?"
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value)
              setSaveError(false)
            }}
          />

          {hasReachedCharacterLimit && (
            <div
              id="custom-instructions-character-limit"
              className="flex items-center justify-between rounded bg-red-50 px-3 py-2 text-xs leading-4 text-red-800"
              role={isOverLimit ? 'alert' : undefined}
            >
              <span>Remaining characters</span>
              <span>{remainingCharacters.toLocaleString('en-US')}</span>
            </div>
          )}

          {containsBlockTag && (
            <div id="custom-instructions-validation">
              <InlineMessage variant="error">Remove the &lt;custom_instructions&gt; tag to save.</InlineMessage>
            </div>
          )}
        </div>
      </ModalContent>
    </Modal>
  )
}
